import { ANALYTICS_EVENTS } from "../analytics-store.tsx";
import { APPS } from "../constants/AppsConstants.ts";
import { getSelectedOrganization } from "../helpers/localStorage.ts";
import { PlaygroundImage } from "../models/playground.ts";
import { modelTypes, TextToImageConfigType } from "../models/text-to-image.ts";
import { isAutomotiveApp, isFoxApps } from "../utils/index.ts";
import { useImageToImageConfig } from "./useImageToImageConfig.tsx";
import { useAppStore } from "./useStores.tsx";

export const useLogEvents = () => {
	const {
		canExpandBackground,
		canRemoveBackground,
		canBlurBackground,
		canApplySolidBackground,
		canGenerateBackgroundByText,
		canApplyAspectRatio,
		canAppleCustomSize,
		canAppleMediumDestination,
		canGenerateForegroundPlacement,
		canGenerateBackgroundByImage,
	} = useImageToImageConfig();

	const { analyticsStore, textToImageStore, playgroundStore } = useAppStore();
	const { logEvent } = analyticsStore;

	const imageEditingParams = {
		orgName: getSelectedOrganization()?.organization.name,
		viewMode: playgroundStore.imageEditingViewMode,
	};

	const logImageDownloadEvents = (images: PlaygroundImage[], fileType: string, resolution?: number) => {
		const { logEvent } = analyticsStore;
		images.forEach((image) => {
			const config = image.config as TextToImageConfigType;
			const additionalParams: { fileType: string; app?: string; model_name?: string } = {
				...imageEditingParams,
				fileType: fileType,
				app: image.type,
			};
			if (image.type === APPS.TEXT_TO_IMAGE || image.type === APPS.TEXT_TO_VECTOR) {
				additionalParams.model_name = config.model_name;

				logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_GENERAL, additionalParams);

				if (config.tailored_model_id) {
					logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_TAILORED_MODELS, {
						tailoredModelId: config.tailored_model_id,
						...additionalParams,
					});
				} else {
					const modelEventMap = {
						[modelTypes.Fast]: ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_BRIA_FAST,
						[modelTypes.Base]: ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_BRIA_BASE,
						[modelTypes.Hd]: ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_BRIA_HD,
					};

					const modelEvent = modelEventMap[config.model as keyof typeof modelEventMap];
					if (modelEvent) {
						logEvent(modelEvent, additionalParams);
					}

					if (textToImageStore.isImageReferenceEnabled()) {
						logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_DOWNLOAD_IMAGE_REFERENCE, additionalParams);
					}

					if (resolution && resolution > 1) {
						logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_INCREASE_RESOLUTION, additionalParams);
					}
				}
			} else if (image.type === APPS.IMAGE_TO_IMAGE) {
				if (isFoxApps()) {
					analyticsStore.logImageToImageEvent(ANALYTICS_EVENTS.IMAGE_DOWNLOAD_ITI, image);
				} else {
					// Background configs events
					canExpandBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_EXPAND_IMAGE, additionalParams);
					canRemoveBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_REMOVE_BACKGROUND, additionalParams);
					canBlurBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_BLUR_BACKGROUND, additionalParams);
					canApplySolidBackground?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_SOLID_BACKGROUND, additionalParams);
					canGenerateBackgroundByText?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_GENERATE_BACKGROUND_BY_TEXT, additionalParams);

					// Size configs events
					canApplyAspectRatio?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_ASPECT_RATIO, additionalParams);
					canAppleCustomSize?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_CUSTOM_SIZE, additionalParams);
					canAppleMediumDestination?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_MEDIUM_DESTINATION, additionalParams);

					// Foreground placement configs events
					canGenerateForegroundPlacement?.() &&
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_DOWNLOAD_FOREGROUND, additionalParams);

					if (resolution && resolution > 1) {
						logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_INCREASE_RESOLUTION, additionalParams);
					}
				}
			} else if (image.type === APPS.PRODUCT_PLACEMENT) {
				analyticsStore.logEvent(ANALYTICS_EVENTS.IMAGE_DOWNLOAD_PP, additionalParams);
			} else {
				analyticsStore.logEvent(ANALYTICS_EVENTS.IMAGE_DOWNLOAD, additionalParams);
			}
		});
	};

	const logImageEditingEvents = async () => {
		if (!isFoxApps()) {
			logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE, imageEditingParams);

			// Background configs events
			canExpandBackground() && logExpandEvent();
			canRemoveBackground() && logRemoveBackgroundEvent();
			canBlurBackground() && logBlurBackgroundEvent();
			canApplySolidBackground() && logSolidBackgroundEvent();
			canGenerateBackgroundByText() && logGenerateByTextEvent();
			canGenerateBackgroundByImage() && logGenerateByImageEvent();

			// Size configs events
			canApplyAspectRatio() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_ASPECT_RATIO, imageEditingParams);
			canAppleCustomSize() && logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_CUSTOM_SIZE, imageEditingParams);
			canAppleMediumDestination() &&
				logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MEDIUM_DESTINATION, imageEditingParams);

			// Foreground placement configs
			canGenerateForegroundPlacement() &&
				logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE_FOREGROUND, imageEditingParams);
		}
	};

	const logGenerateByTextEvent = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE_BACKGROUND_BY_TEXT, imageEditingParams);
	};

	const logGenerateByImageEvent = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_GENERATE_BACKGROUND_BY_IMAGE, imageEditingParams);
	};

	const logSolidBackgroundEvent = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_SOLID_BACKGROUND, imageEditingParams);
	};

	const logRemoveBackgroundEvent = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_REMOVE_BACKGROUND, imageEditingParams);
	};

	const logBlurBackgroundEvent = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_BLUR_BACKGROUND, imageEditingParams);
	};

	const logEraseObjectEvent = () => {
		const eventName = isAutomotiveApp() ? ANALYTICS_EVENTS.AUTOMOTIVE_ERASE : ANALYTICS_EVENTS.IMAGE_EDITING_ERASE;
		logEvent(eventName, imageEditingParams);
	};

	const logGenerativeFillEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_GENERATIVE_FILL
			: ANALYTICS_EVENTS.IMAGE_EDITING_GENERATIVE_FILL;
		logEvent(eventName, imageEditingParams);
	};

	const logExpandEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_EXPAND_IMAGE
			: ANALYTICS_EVENTS.IMAGE_EDITING_EXPAND_IMAGE;
		logEvent(eventName, imageEditingParams);
	};
	const logCropEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_CROP
			: ANALYTICS_EVENTS.IMAGE_EDITING_CROP_IMAGE;
		logEvent(eventName, imageEditingParams);
	};

	const logIncreaseResolutionEvent = () => {
		const eventName = isAutomotiveApp()
			? ANALYTICS_EVENTS.AUTOMOTIVE_INCREASE_RESOLUTION
			: ANALYTICS_EVENTS.IMAGE_EDITING_INCREASE_RESOLUTION;
		logEvent(eventName, imageEditingParams);
	};

	const logAutomotiveEvents = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE, imageEditingParams);

		// Background configs events
		canExpandBackground() && logExpandEvent();
		canRemoveBackground() && logAutomotiveRemoveBackgroundEvent();
		canBlurBackground() && logAutomotiveBlurBackgroundEvent();
		canApplySolidBackground() && logAutomotiveSolidBackgroundEvent();
		canGenerateBackgroundByText() && logAutomotiveGenerateByTextEvent();
		canGenerateBackgroundByImage() && logAutomotiveGenerateByImageEvent();

		// Size configs events
		canApplyAspectRatio() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_ASPECT_RATIO, imageEditingParams);
		canAppleCustomSize() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_CUSTOM_SIZE, imageEditingParams);
		canAppleMediumDestination() && logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_MEDIUM_DESTINATION, imageEditingParams);

		// Foreground placement configs
		canGenerateForegroundPlacement() &&
			logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE_FOREGROUND, imageEditingParams);
	};

	const logAutomotiveGenerateByTextEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE_BACKGROUND_BY_TEXT, imageEditingParams);
	};

	const logAutomotiveGenerateByImageEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_GENERATE_BACKGROUND_BY_IMAGE, imageEditingParams);
	};

	const logAutomotiveSolidBackgroundEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_SOLID_BACKGROUND, imageEditingParams);
	};

	const logAutomotiveRemoveBackgroundEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_REMOVE_BACKGROUND, imageEditingParams);
	};

	const logAutomotiveBlurBackgroundEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_BLUR_BACKGROUND, imageEditingParams);
	};

	const logTiresRefinementEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_TIRES_REFINEMENT, imageEditingParams);
	};

	const logReflectionsEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_REFLECTIONS, imageEditingParams);
	};

	const logEffectsEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_EFFECTS, imageEditingParams);
	};

	const logHarmonizationEvent = () => {
		logEvent(ANALYTICS_EVENTS.AUTOMOTIVE_HARMONIZATION, imageEditingParams);
	};

	// Image Generation Integration Events
	const logImageGenerationMcpSidebarClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_SIDEBAR_CLICK);
	};

	const logImageGenerationMcpTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_TAB_CLICK);
	};

	const logImageGenerationMcpCopyUrlClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_COPY_URL_CLICK);
	};

	const logImageGenerationMcpQuickTestingTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_QUICK_TESTING_TAB_CLICK);
	};

	const logImageGenerationMcpCreateApiTokenClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_CREATE_API_TOKEN_CLICK);
	};

	const logImageGenerationMcpCopyApiTokenClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_COPY_API_TOKEN_CLICK);
	};

	const logImageGenerationMcpVideoExampleClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_VIDEO_EXAMPLE_CLICK);
	};

	const logImageGenerationMcpApiTokenDocsClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_API_TOKEN_DOCS_CLICK);
	};

	const logImageGenerationMcpBearerAuthDocsClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_MCP_BEARER_AUTH_DOCS_CLICK);
	};

	const logImageGenerationComfyUSidebarClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_COMFYUI_SIDEBAR_CLICK);
	};

	const logImageGenerationComfyUTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_COMFYUI_TAB_CLICK);
	};

	const logImageGenerationComfyUCopyApiTokenClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_COMFYUI_COPY_API_TOKEN_CLICK);
	};

	const logImageGenerationComfyNodeClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_COMFYUI_NODE_CLICK);
	};

	const logImageGenerationComfyUWorkflowClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_COMFYUI_WORKFLOW_CLICK);
	};

	const logImageGenerationFalClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_FAL_CLICK);
	};

	const logImageGenerationInvokeClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_INVOKE_CLICK);
	};

	const logImageGenerationAwsClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_AWS_CLICK);
	};

	const logImageGenerationAzureClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_AZURE_CLICK);
	};

	const logImageGenerationFigmaClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_FIGMA_CLICK);
	};

	const logImageGenerationPhotoshopClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_PHOTOSHOP_CLICK);
	};

	const logImageGenerationFigmaTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_FIGMA_TAB_CLICK);
	};

	const logImageGenerationFigmaPluginDownloadClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_FIGMA_PLUGIN_DOWNLOAD_CLICK);
	};

	const logImageGenerationFigmaVideoExampleClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_FIGMA_VIDEO_EXAMPLE_CLICK);
	};

	const logImageGenerationFigmaPluginDownload = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_FIGMA_PLUGIN_DOWNLOAD);
	};

	const logImageGenerationPhotoshopGoToMarketplace = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_GENERATION_PHOTOSHOP_GO_TO_MARKETPLACE);
	};

	// Image Editing Integration Events
	const logImageEditingMcpSidebarClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_SIDEBAR_CLICK);
	};

	const logImageEditingMcpTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_TAB_CLICK);
	};

	const logImageEditingFigmaClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_FIGMA_CLICK);
	};

	const logImageEditingPhotoshopClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_PHOTOSHOP_CLICK);
	};

	const logImageEditingFigmaTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_FIGMA_TAB_CLICK);
	};

	const logImageEditingFigmaPluginDownloadClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_FIGMA_PLUGIN_DOWNLOAD_CLICK);
	};

	const logImageEditingFigmaVideoExampleClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_FIGMA_VIDEO_EXAMPLE_CLICK);
	};

	const logImageEditingFigmaPluginDownload = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_FIGMA_PLUGIN_DOWNLOAD);
	};

	const logImageEditingPhotoshopGoToMarketplace = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_PHOTOSHOP_GO_TO_MARKETPLACE);
	};

	// Tailored Generation Integration Events
	const logTailoredGenerationFigmaTabClick = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_FIGMA_TAB_CLICK);
	};

	const logTailoredGenerationFigmaPluginDownloadClick = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_FIGMA_PLUGIN_DOWNLOAD_CLICK);
	};

	const logTailoredGenerationFigmaVideoExampleClick = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_FIGMA_VIDEO_EXAMPLE_CLICK);
	};

	const logImageEditingComfyUSidebarClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_COMFYUI_SIDEBAR_CLICK);
	};

	const logImageEditingComfyUTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_COMFYUI_TAB_CLICK);
	};

	// Tailored Generation Integration Events
	const logTailoredGenerationFigmaClick = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_FIGMA_CLICK);
	};

	const logTailoredGenerationPhotoshopClick = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_PHOTOSHOP_CLICK);
	};

	const logTailoredGenerationFigmaPluginDownload = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_FIGMA_PLUGIN_DOWNLOAD);
	};

	const logTailoredGenerationPhotoshopGoToMarketplace = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_PHOTOSHOP_GO_TO_MARKETPLACE);
	};

	const logTailoredGenerationComfyUSidebarClick = () => {
		logEvent(ANALYTICS_EVENTS.TAILORED_GENERATION_COMFYUI_SIDEBAR_CLICK);
	};

	const logImageEditingComfyUCopyApiTokenClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_COMFYUI_COPY_API_TOKEN_CLICK);
	};

	const logImageEditingComfyNodeClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_COMFYUI_NODE_CLICK);
	};

	const logImageEditingComfyUWorkflowClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_COMFYUI_WORKFLOW_CLICK);
	};

	const logImageEditingMcpCopyUrlClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_COPY_URL_CLICK);
	};

	const logImageEditingMcpQuickTestingTabClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_QUICK_TESTING_TAB_CLICK);
	};

	const logImageEditingMcpCreateApiTokenClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_CREATE_API_TOKEN_CLICK);
	};

	const logImageEditingMcpCopyApiTokenClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_COPY_API_TOKEN_CLICK);
	};

	const logImageEditingMcpVideoExampleClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_VIDEO_EXAMPLE_CLICK);
	};

	const logImageEditingMcpApiTokenDocsClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_API_TOKEN_DOCS_CLICK);
	};

	const logImageEditingMcpBearerAuthDocsClick = () => {
		logEvent(ANALYTICS_EVENTS.IMAGE_EDITING_MCP_BEARER_AUTH_DOCS_CLICK);
	};

	const logGetApiKeyButtonClicked = () => {
		logEvent(ANALYTICS_EVENTS.GET_API_KEY_BUTTON_CLICKED);
	};

	return {
		logImageDownloadEvents,
		logImageEditingEvents,
		logEraseObjectEvent,
		logGenerativeFillEvent,
		logExpandEvent,
		logCropEvent,
		logIncreaseResolutionEvent,
		logAutomotiveEvents,
		logTiresRefinementEvent,
		logReflectionsEvent,
		logEffectsEvent,
		logHarmonizationEvent,
		logGenerateByTextEvent,
		logGenerateByImageEvent,
		logSolidBackgroundEvent,
		logRemoveBackgroundEvent,
		logBlurBackgroundEvent,
		logAutomotiveGenerateByTextEvent,
		logAutomotiveGenerateByImageEvent,
		logAutomotiveSolidBackgroundEvent,
		logAutomotiveRemoveBackgroundEvent,
		logGetApiKeyButtonClicked,
		// Image Generation Integration Events
		logImageGenerationMcpSidebarClick,
		logImageGenerationMcpTabClick,
		logImageGenerationMcpCopyUrlClick,
		logImageGenerationMcpQuickTestingTabClick,
		logImageGenerationMcpCreateApiTokenClick,
		logImageGenerationMcpCopyApiTokenClick,
		logImageGenerationMcpVideoExampleClick,
		logImageGenerationMcpApiTokenDocsClick,
		logImageGenerationMcpBearerAuthDocsClick,
		logImageGenerationComfyUSidebarClick,
		logImageGenerationComfyUTabClick,
		logImageGenerationComfyUCopyApiTokenClick,
		logImageGenerationComfyNodeClick,
		logImageGenerationComfyUWorkflowClick,
		logImageGenerationFalClick,
		logImageGenerationInvokeClick,
		logImageGenerationAwsClick,
		logImageGenerationAzureClick,
		logImageGenerationFigmaClick,
		logImageGenerationFigmaTabClick,
		logImageGenerationFigmaPluginDownloadClick,
		logImageGenerationFigmaVideoExampleClick,
		logImageGenerationPhotoshopClick,
		logImageGenerationFigmaPluginDownload,
		logImageGenerationPhotoshopGoToMarketplace,
		// Image Editing Integration Events
		logImageEditingMcpSidebarClick,
		logImageEditingMcpTabClick,
		logImageEditingFigmaClick,
		logImageEditingFigmaTabClick,
		logImageEditingFigmaPluginDownloadClick,
		logImageEditingFigmaVideoExampleClick,
		logImageEditingPhotoshopClick,
		logImageEditingFigmaPluginDownload,
		// Tailored Generation Integration Events
		logTailoredGenerationFigmaTabClick,
		logTailoredGenerationFigmaPluginDownloadClick,
		logTailoredGenerationFigmaVideoExampleClick,
		logImageEditingPhotoshopGoToMarketplace,
		logImageEditingComfyUSidebarClick,
		logImageEditingComfyUTabClick,
		logImageEditingComfyUCopyApiTokenClick,
		logImageEditingComfyNodeClick,
		logImageEditingComfyUWorkflowClick,
		logImageEditingMcpCopyUrlClick,
		logImageEditingMcpQuickTestingTabClick,
		logImageEditingMcpCreateApiTokenClick,
		logImageEditingMcpCopyApiTokenClick,
		logImageEditingMcpVideoExampleClick,
		logImageEditingMcpApiTokenDocsClick,
		logImageEditingMcpBearerAuthDocsClick,
		// Tailored Generation Integration Events
		logTailoredGenerationFigmaClick,
		logTailoredGenerationPhotoshopClick,
		logTailoredGenerationFigmaPluginDownload,
		logTailoredGenerationPhotoshopGoToMarketplace,
		logTailoredGenerationComfyUSidebarClick,
	};
};
